import { useEffect, useMemo, useState, ReactElement } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PlaceIcon from "@mui/icons-material/Place";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import SchoolIcon from "@mui/icons-material/School";
import ChurchIcon from "@mui/icons-material/Church";
import CakeIcon from "@mui/icons-material/Cake";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";

export type TimelineIconKey =
  | "birth"
  | "graduation"
  | "death"
  | "celebration"
  | "love";

export interface TimelineEvent {
  id: number;
  year?: string | number;
  date: string;
  title: string;
  location: string;
  description?: string;
  display_status: "private" | "public";
  icon: TimelineIconKey;
  color?: string;
  deceased?: number;
}

const ICON_OPTIONS: { value: TimelineIconKey; label: string; icon: ReactElement }[] = [
  { value: "birth", label: "Birth", icon: <ChildCareIcon /> },
  { value: "graduation", label: "Graduation", icon: <SchoolIcon /> },
  { value: "death", label: "Death", icon: <ChurchIcon /> },
  { value: "celebration", label: "Celebration", icon: <CakeIcon /> },
  { value: "love", label: "Love", icon: <FavoriteIcon /> },
];

const getIcon = (iconName?: string) => {
  const found = ICON_OPTIONS.find((o) => o.value === iconName?.toLowerCase());
  return found ? found.icon : <ChildCareIcon />;
};

const formatTimelineDate = (dateStr?: string) => {
  if (!dateStr) return { year: "—", prettyDate: "" };
  const [y, m, d] = dateStr.split("-").map((x) => Number(x));
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };
  return { year: String(y || "—"), prettyDate: `${months[(m || 1) - 1]} ${ordinal(d || 1)}` };
};

const formatFullDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const { year, prettyDate } = formatTimelineDate(dateStr);
  return prettyDate ? `${prettyDate}, ${year}` : year;
};

interface TimelineTabProps {
  deceasedId?: number | null;
  deceasedFirstName?: string;
  accentColor?: string;
  isAdmin?: boolean;
  /** "standalone" (default) wraps content in the white motion/Card chrome
   * used across template-1's other tabs. "embedded" renders just the
   * content, matching template-2's own floating tab design (e.g. Memories). */
  variant?: "standalone" | "embedded";
}

export default function TimelineTab({
  deceasedId,
  deceasedFirstName,
  accentColor,
  isAdmin = false,
  variant = "standalone",
}: TimelineTabProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TimelineEvent | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIcon, setFormIcon] = useState<TimelineIconKey>("birth");
  const [formDisplayStatus, setFormDisplayStatus] = useState<"private" | "public">("public");
  const [saving, setSaving] = useState(false);

  const brandColor = accentColor || reduxTheme.primary_colors?.[400] || "#6B4A3A";

  useEffect(() => {
    if (!deceasedId) {
      setLoading(false);
      return;
    }

    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<
          TimelineEvent[] | { results: TimelineEvent[] }
        >(`/api/client-portal/timeline-events/?deceased_id=${deceasedId}`);
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.results ?? [];
        const filtered = isAdmin ? data : data.filter((e) => e.display_status === "public");
        setEvents(filtered);
      } catch (err) {
        console.error("Failed to fetch timeline events", err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [deceasedId, isAdmin]);

  // Newest first, matching the reference client-facing portal
  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => (a.date > b.date ? -1 : 1)),
    [events]
  );

  const openAddModal = () => {
    setEditingItem(null);
    setFormTitle("");
    setFormDate("");
    setFormLocation("");
    setFormDescription("");
    setFormIcon("birth");
    setFormDisplayStatus("public");
    setModalOpen(true);
  };

  const openEditModal = (event: TimelineEvent) => {
    setEditingItem(event);
    setFormTitle(event.title);
    setFormDate(event.date);
    setFormLocation(event.location);
    setFormDescription(event.description || "");
    setFormIcon((event.icon as TimelineIconKey) || "birth");
    setFormDisplayStatus(event.display_status || "public");
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formDate.trim() || !deceasedId) return;
    setSaving(true);
    try {
      const payload = {
        title: formTitle,
        date: formDate,
        year: formDate.substring(0, 4),
        location: formLocation,
        description: formDescription,
        icon: formIcon,
        color: "#724E41",
        display_status: formDisplayStatus,
        deceased: deceasedId,
      };

      if (editingItem) {
        const res = await apiService.patch<TimelineEvent>(
          `/api/client-portal/timeline-events/${editingItem.id}/`,
          payload
        );
        setEvents((prev) => prev.map((e) => (e.id === editingItem.id ? res.data : e)));
        showSuccessToast("Timeline event updated successfully.");
      } else {
        const res = await apiService.post<TimelineEvent>(
          `/api/client-portal/timeline-events/`,
          payload
        );
        setEvents((prev) => [...prev, res.data]);
        showSuccessToast("Timeline event added successfully.");
      }
      handleClose();
    } catch {
      showErrorToast("Failed to save timeline event.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const spinner = (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress style={{ color: brandColor }} />
      </Box>
    );
    return variant === "standalone" ? (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card sx={{ height: "100%", boxShadow: 0, borderRadius: "20px", padding: "1.2rem" }}>
          {spinner}
        </Card>
      </motion.div>
    ) : (
      spinner
    );
  }

  const emptyText = isAdmin
    ? `Tell Us A Bit More About ${deceasedFirstName || "Their"}'s Life Journey. Add meaningful events to the timeline.`
    : `No timeline moments have been added for ${deceasedFirstName || "them"} yet.`;

  const content = (
    <>
      {/* Admin: Add button header */}
      {isAdmin && (
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button
            variant="contained"
            onClick={openAddModal}
            sx={{
              backgroundColor: brandColor,
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { opacity: 0.9 },
            }}
          >
            Add Reflection
          </Button>
        </Box>
      )}

      {events.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={3}>
          <Typography variant="body1" sx={{ color: "text.secondary", fontStyle: "italic", textAlign: "center" }}>
            {emptyText}
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              onClick={openAddModal}
              sx={{
                backgroundColor: brandColor,
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { opacity: 0.9 },
              }}
            >
              Add Reflection
            </Button>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={4}>
          {sortedEvents.map((event) => (
            <Box key={event.id} sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
              {/* Icon + Date */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.75,
                  flexShrink: 0,
                  width: 72,
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: brandColor,
                    color: "#ffffff",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                    "& svg": { fontSize: 22 },
                  }}
                >
                  {getIcon(event.icon)}
                </Avatar>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: brandColor, textAlign: "center", lineHeight: 1.3 }}
                >
                  {formatFullDate(event.date) || event.year}
                </Typography>
              </Box>

              {/* Content Card */}
              <Card
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
                  backgroundColor: reduxTheme.primary_colors?.[900] || "#F9F9F9",
                  borderLeft: `4px solid ${brandColor}`,
                  position: "relative",
                }}
              >
                {/* Admin edit button */}
                {isAdmin && (
                  <IconButton
                    size="small"
                    onClick={() => openEditModal(event)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}

                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: reduxTheme.primary_colors?.[100] || brandColor, mb: 1.5, pr: isAdmin ? 4 : 0 }}
                  >
                    {event.title}
                  </Typography>

                  {event.location && (
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1.5, color: "text.secondary" }}>
                      <PlaceIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{event.location}</Typography>
                    </Box>
                  )}

                  {event.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {event.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Write about {deceasedFirstName || "their"}&apos;s Event
          <IconButton onClick={handleClose} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Build a beautiful timeline of {deceasedFirstName || "their"}&apos;s life by capturing
            special memories, achievements, and unforgettable moments.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Timeline Title"
              required
              fullWidth
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="e.g. Graduated from university"
            />
            <TextField
              label="Date"
              required
              fullWidth
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Location"
              fullWidth
              value={formLocation}
              onChange={(e) => setFormLocation(e.target.value)}
              placeholder="e.g. Harare, Zimbabwe"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Write a short description…"
            />

            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                Choose Icon
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {ICON_OPTIONS.map((opt) => {
                  const active = formIcon === opt.value;
                  return (
                    <IconButton
                      key={opt.value}
                      onClick={() => setFormIcon(opt.value)}
                      title={opt.label}
                      sx={{
                        width: 52,
                        height: 52,
                        border: active ? `2px solid ${brandColor}` : "1px solid #CBD5E1",
                        bgcolor: "#fff",
                        color: brandColor,
                      }}
                    >
                      {opt.icon}
                    </IconButton>
                  );
                })}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formDisplayStatus === "public"}
                  onChange={(e) =>
                    setFormDisplayStatus(e.target.checked ? "public" : "private")
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: brandColor },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: brandColor,
                    },
                  }}
                />
              }
              label="Visible to everyone on this memorial page"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formTitle.trim() || !formDate.trim()}
            sx={{
              textTransform: "none",
              backgroundColor: brandColor,
              "&:hover": { opacity: 0.9 },
            }}
          >
            {saving ? "Saving…" : editingItem ? "Save Changes" : "Add Reflection"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return variant === "standalone" ? (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          height: "100%",
          boxShadow: 0,
          borderRadius: "20px",
          padding: "1.2rem",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {content}
      </Card>
    </motion.div>
  ) : (
    <Box sx={{ width: "100%", py: 3, px: { xs: 1, md: 3 } }}>{content}</Box>
  );
}
